import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import type { EventReportPayload } from './eventReportApi';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useT } from '@/lib/i18n/useT';
import { REPORT_SECTIONS, useReportSections, type ReportSection } from './reportSections';
import { SurveyDemographics } from '@/features/demographics/SurveyDemographics';
import { useRightSidebar } from '@/components/layout/RightSidebar';
import type { EventFacts } from '@/features/insights/factsApi';
import { bigOf, env, fmtInt, fmtPct, fmtUsd, funnelOf, goalOf, ledgerOf, money, phaseOf, throughputOf } from '@/features/insights/engine';
import { HeroTiles } from '@/features/insights/charts/HeroTiles';
import { FunnelBars } from '@/features/insights/charts/FunnelBars';
import { LedgerBars } from '@/features/insights/charts/LedgerBars';
import { PremiumsDelivered } from '@/features/insights/charts/PremiumsDelivered';
import { UnaccountedCard } from '@/features/insights/charts/UnaccountedCard';
import { ThroughputTimeline } from '@/features/insights/charts/ThroughputTimeline';
import { CostCells } from '@/features/insights/charts/CostCells';
import { AtlediRead } from '@/features/insights/charts/AtlediRead';
import { useScrollRamp } from '@/features/insights/motion/useScrollRamp';
import { weatherStamps } from '@/features/insights/weatherStamps';

/**
 * Caso 02 — Shared visual report. Rendered identically in the admin
 * `EventReportPage` and the public-share `PublicEventReportPage`.
 *
 * Con `facts` (informe interno) sigue el diseño de Xotski Dashboards, bloque
 * a bloque: lo que hizo la mercancía, throughput y clima, la lectura de
 * Atledi, premiums, el libro, lo sin destino, el embudo, el coste, la
 * demografía y la salud del dato. Cada bloque se llena al entrar en pantalla.
 * Sin `facts` —el informe público— se queda con el payload compartible.
 *
 * Print-friendly: hides chrome via `@media print` in the parent.
 */
export function EventReportView({
  report,
  variant,
  facts,
}: {
  report: EventReportPayload;
  variant: 'internal' | 'public';
  facts?: EventFacts;
}) {
  const t = useT();
  const navigate = useNavigate();
  const openRail = useRightSidebar((s) => s.setOpen);
  const dates =
    report.event.startDate || report.event.endDate
      ? `${dayjs(report.event.startDate ?? undefined).format('MMM D, YYYY')} → ${dayjs(report.event.endDate ?? undefined).format('MMM D, YYYY')}`
      : '';
  // Task 5.4 — every block below can be hidden; the choice persists and is
  // honoured by print. Subscribe to `hidden` (state), not to `isVisible`.
  const hiddenSections = useReportSections((st) => st.hidden);
  const visible = (id: ReportSection) => !hiddenSections[id];

  const ledger = facts ? ledgerOf(facts.ledger) : null;
  const funnel = facts ? funnelOf(facts.funnel, { surveyed: facts.stations.withSurvey > 0 }) : null;
  const throughput = facts ? throughputOf(facts.throughput) : [];
  const phase = facts ? phaseOf(facts) : 'done';
  const live = phase === 'live';
  const claimRate = facts && facts.funnel.interactions ? (ledger?.delivered ?? 0) / facts.funnel.interactions : 0;
  const captures = throughput.reduce((a, s) => a + s.total, 0);

  const counts: Partial<Record<ReportSection, string>> = facts && ledger
    ? {
        reach: fmtInt(facts.funnel.interactions),
        prizes: fmtInt(ledger.delivered),
        throughput: fmtInt(captures),
        costs: ledger.distributedValue.state === 'PENDING' ? t('dash.pending').toLowerCase() : fmtUsd(ledger.distributedValue.value),
        topAnswers: report.topAnswers.length ? String(report.topAnswers.length) : t('eventReport.show.none'),
        demographics: facts.funnel.responses ? fmtInt(facts.funnel.responses) : t('eventReport.show.none'),
      }
    : {};

  return (
    <div className="space-y-5" data-report-view>
      <header className="border-b pb-4" style={{ borderColor: 'var(--color-dash-border)' }}>
        {report.branding?.logo && <img src={report.branding.logo} alt={report.branding.campaignName ?? ''} className="mb-3 h-10 w-auto" />}
        <h1 className="text-[30px] font-bold leading-[1.15] tracking-[-0.02em]">{report.event.name}</h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {report.event.address ?? ''} {dates && `· ${dates}`}
        </p>
        {report.branding?.campaignName && (
          <Badge tone="accent" className="mt-2">
            {report.branding.campaignName}
          </Badge>
        )}
        {/* Sólo en papel: cuándo se generó, para que el PDF se pueda fechar. */}
        <p className="mt-2 hidden text-[11px] text-[var(--color-fg-muted)] print:block">
          {t('eventReport.printedOn', { date: dayjs().format('MMM D, YYYY HH:mm') })}
        </p>
      </header>

      <SectionToggles counts={counts} />

      {/* Sin hechos (informe público): los cuatro KPIs de alcance de siempre. */}
      {visible('reach') && !facts && (
        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi label={t('eventReport.reach.interactions')} value={report.reach.interactionsTotal} />
          <Kpi label={t('eventReport.reach.last24h')} value={report.reach.interactionsLast24h} />
          <Kpi label={t('eventReport.reach.distinctContacts')} value={report.reach.distinctContacts} />
          <Kpi label={t('eventReport.reach.leads')} value={report.reach.leadsCaptured} highlight />
        </section>
      )}

      {/* CH-15 · Cero interacciones no es «sin datos para estos filtros»: es
          que nada se capturó. Se dice arriba del todo, antes de las cifras. */}
      {facts && facts.funnel.interactions === 0 && (
        <div
          role="status"
          data-testid="report-not-captured"
          className="rounded-lg border border-[var(--color-warn)] bg-[rgb(234_179_8/0.08)] px-4 py-3 text-sm"
        >
          <strong className="text-[var(--color-warn)]">{t('eventReport.notCaptured.title')}</strong>{' '}
          {t('eventReport.notCaptured.body')}
        </div>
      )}

      {/* 1 · Lo que hizo la mercancía */}
      {visible('prizes') && ledger && facts && (
        <HeroTiles
          title={t('eventReport.hero.title')}
          chip={
            facts.closing
              ? t('eventReport.hero.reconciled', { date: dayjs(facts.closing.closedAt).format('MMM D, YYYY') })
              : live
                ? t('eventReport.hero.provisional')
                : t('eventReport.hero.open', { n: fmtInt(ledger.inPlay) })
          }
          chipTone={facts.closing ? 'accent' : 'warn'}
          tiles={[
            {
              label: t('eventReport.hero.unitsOut'),
              value: fmtInt(ledger.consumed),
              number: ledger.consumed,
              format: fmtInt,
              hint: t('eventReport.hero.unitsOutHint', { allocated: fmtInt(ledger.allocated), inPlay: fmtInt(ledger.inPlay) }),
              tone: 'accent',
            },
            {
              label: t('eventReport.hero.delivered'),
              value: fmtInt(ledger.delivered),
              number: ledger.delivered,
              format: fmtInt,
              hint: t('eventReport.hero.deliveredHint', { p: bigOf(ledger.redemption, '—'), won: fmtInt(ledger.won) }),
            },
            {
              label: t('eventReport.hero.unaccounted'),
              value: fmtInt(ledger.unaccounted),
              number: ledger.unaccounted,
              format: fmtInt,
              hint: t('eventReport.hero.unaccountedHint', { p: bigOf(ledger.unaccountedRate, '—') }),
              tone: ledger.unaccounted > 0 ? 'warn' : 'default',
            },
            {
              label: t('eventReport.hero.value'),
              value: bigOf(ledger.distributedValue, t('dash.pending')),
              number: ledger.distributedValue.value,
              format: ledger.distributedValue.state === 'PENDING' ? undefined : fmtUsd,
              pending: ledger.distributedValue.state === 'PENDING',
              hint:
                ledger.distributedValue.state === 'PENDING'
                  ? t('eventReport.hero.valuePending', { items: ledger.missingCost.map((r) => r.name).join(', ') })
                  : t('eventReport.hero.valueHint', { n: fmtInt(ledger.delivered) }),
              tone: ledger.distributedValue.state === 'PENDING' ? 'warn' : 'default',
              source:
                ledger.distributedValue.state === 'PENDING'
                  ? t('eventReport.hero.addCost')
                  : t('eventReport.hero.costed', { costed: ledger.costedRows, n: ledger.rows.length }),
              onSource: () => navigate('/stock'),
            },
          ]}
        />
      )}

      {/* 2 · Throughput y el clima como covariable */}
      {visible('throughput') && facts && (
        <div className="flex flex-col gap-4">
          <ThroughputTimeline
            series={throughput}
            title={t('eventReport.throughput.title')}
            stamps={weatherStamps(facts.weather)}
            claimRate={claimRate}
            capLabel={
              throughput.length
                ? t('dash.thru.capLabel', { n: fmtInt(Math.max(...throughput.map((s) => s.peak))), stations: facts.stations.total })
                : undefined
            }
          />
          <WeatherNote facts={facts} />
        </div>
      )}

      {/* 3 · La lectura de Atledi */}
      {facts && ledger && (
        <AtlediRead
          kicker={t('dash.atledi.eventRead')}
          stamp={t('dash.atledi.stamp', { n: REPORT_SECTIONS.filter((id) => visible(id)).length, total: REPORT_SECTIONS.length })}
          text={atlediText(t, facts, ledger)}
          prompt={live ? t('dash.atledi.promptLive') : t('dash.atledi.promptClosed')}
          openLabel={t('dash.atledi.open')}
          onOpen={() => openRail(true)}
        />
      )}

      {/* 4 · Premiums, el libro y lo sin destino */}
      {visible('prizes') && ledger && facts && ledger.rows.length > 0 && (
        <>
          <PremiumsDelivered totals={ledger} goal={goalOf(facts, 'prizesDelivered')} />
          <LedgerBars
            rows={ledger.rows}
            totals={ledger}
            kicker={t('eventReport.ledger.kicker')}
            title={t('eventReport.ledger.title')}
            basis={ledger.inPlay > 0 ? t('eventReport.ledger.basisOpen', { n: fmtInt(ledger.inPlay) }) : t('eventReport.ledger.basisClosed')}
          />
          <UnaccountedCard totals={ledger} />
        </>
      )}
      {visible('prizes') && ledger && ledger.rows.length === 0 && (
        <Card>
          <CardContent className="py-6 text-center text-xs text-[var(--color-fg-subtle)]">{t('eventReport.ledger.empty')}</CardContent>
        </Card>
      )}

      {/* 5 · El embudo de captura */}
      {visible('reach') && funnel && facts && (
        <FunnelBars
          steps={funnel}
          title={t('eventReport.funnel.title')}
          aside={t('eventReport.funnel.aside')}
          note={facts.stations.withSurvey === 0 ? t('eventReport.funnel.noSurvey') : t('eventReport.funnel.note')}
        />
      )}

      {/* 6 · Coste, cerrado por defecto */}
      {visible('costs') && ledger && facts && (
        <CostCells
          cells={[
            {
              label: t('eventReport.costs.distributedValue'),
              metric: ledger.distributedValue,
              note: ledger.missingCost.length
                ? t('eventReport.costs.valueNote', { items: ledger.missingCost.map((r) => r.name).join(', ') })
                : t('eventReport.costs.valueOk'),
            },
            {
              label: t('eventReport.costs.perLead'),
              metric: money(
                ledger.distributedValue.value != null && facts.funnel.leads ? ledger.distributedValue.value / facts.funnel.leads : null,
                ledger.distributedValue.state === 'PENDING' ? 'distributedValue' : 'leads',
              ),
            },
            {
              label: t('eventReport.costs.perInteraction'),
              metric: money(
                ledger.distributedValue.value != null && facts.funnel.interactions ? ledger.distributedValue.value / facts.funnel.interactions : null,
                ledger.distributedValue.state === 'PENDING' ? 'distributedValue' : 'interactions',
              ),
            },
            { label: t('eventReport.costs.redemption'), metric: ledger.redemption },
            { label: t('eventReport.costs.captureRate'), metric: env(null, null, 'footprintTraffic') },
          ]}
        />
      )}

      {/* Los cuatro bloques del payload compartible, cuando no hay hechos. */}
      {!ledger && (
        <section className="grid gap-4 lg:grid-cols-2">
          {visible('prizes') && (
            <Card>
              <CardHeader>
                <CardTitle>{t('eventReport.prizes.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 text-sm">
                <Row label={t('eventReport.prizes.allocated')} value={report.prizes.totalAllocated} />
                <Row label={t('eventReport.prizes.distributed')} value={report.prizes.totalDistributed} />
                <Row label={t('eventReport.prizes.remaining')} value={report.prizes.totalRemaining} />
                <Row label={t('eventReport.prizes.value')} value={`$${report.prizes.distributedValue.toFixed(2)}`} />
              </CardContent>
            </Card>
          )}
          {visible('costs') && (
            <Card>
              <CardHeader>
                <CardTitle>{t('eventReport.costs.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 text-sm">
                <Row label={t('eventReport.costs.perInteraction')} value={report.costs.costPerInteraction != null ? `$${report.costs.costPerInteraction.toFixed(2)}` : '—'} />
                <Row label={t('eventReport.costs.perLead')} value={report.costs.costPerLead != null ? `$${report.costs.costPerLead.toFixed(2)}` : '—'} />
              </CardContent>
            </Card>
          )}
        </section>
      )}

      {/* 7 · Demografía leída de las encuestas (interno) o cubos del payload (público) */}
      {visible('demographics') && variant === 'internal' && <SurveyDemographics eventIds={[report.event.id]} />}
      {visible('demographics') && variant === 'public' && Object.keys(report.demographics).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('eventReport.demographics.title')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(report.demographics).map(([key, buckets]) => (
              <div key={key}>
                <h4 className="text-xs font-semibold uppercase text-[var(--color-fg-muted)]">{key}</h4>
                <ul className="mt-1 space-y-1">
                  {buckets.slice(0, 6).map((b) => (
                    <li key={b.bucket} className="flex items-center justify-between text-xs">
                      <span>{b.bucket}</span>
                      <span className="tabular-nums">{b.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* 8 · Respuestas más frecuentes */}
      {visible('topAnswers') && report.topAnswers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('eventReport.topAnswers.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {report.topAnswers.map((q) => (
              <div key={q.componentId}>
                <h4 className="text-sm font-semibold">{q.label}</h4>
                <ul className="mt-1 space-y-1">
                  {q.options.map((o) => (
                    <li key={o.value} className="flex items-center justify-between text-xs text-[var(--color-fg-muted)]">
                      <span>{o.value}</span>
                      <span className="tabular-nums">
                        {o.count} ({o.percent}%)
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* 9 · Salud del dato: de las filas brutas a las personas que se pueden
          defender, y lo que aún no se puede computar y por qué. */}
      {visible('health') && facts && ledger && <DataHealth facts={facts} ledger={ledger} />}

      <footer className="flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-xs" style={{ borderColor: 'var(--color-dash-border)', color: 'var(--color-dash-fg-muted)' }}>
        <span>
          {t('eventReport.footer.part')} <strong style={{ color: 'var(--color-dash-olive)' }}>Onxite</strong>
        </span>
        <span>{variant === 'public' ? t('eventReport.publicFooter') : t('eventReport.footer.frames')}</span>
      </footer>
    </div>
  );
}

/** El texto de la lectura de Atledi: compuesto con las cifras, no inventado. */
function atlediText(t: ReturnType<typeof useT>, facts: EventFacts, ledger: ReturnType<typeof ledgerOf>): string {
  const ur = bigOf(ledger.unaccountedRate, '—');
  if (!facts.funnel.responses) {
    return t('dash.atledi.textNoSurvey', {
      out: fmtInt(ledger.consumed),
      delivered: fmtInt(ledger.delivered),
      unaccounted: fmtInt(ledger.unaccounted),
      p: ur,
    });
  }
  return t('dash.atledi.textSurvey', {
    delivered: fmtInt(ledger.delivered),
    responses: fmtInt(facts.funnel.responses),
    interactions: fmtInt(facts.funnel.interactions),
    redemption: bigOf(ledger.redemption, '—'),
    p: ur,
    pending: fmtInt(ledger.pending),
  });
}

function WeatherNote({ facts }: { facts: EventFacts }) {
  const t = useT();
  const days = facts.weather;
  const f = (c: number | null) => (c == null ? '—' : `${Math.round((c * 9) / 5 + 32)}°F`);
  const desc = (code: number | null) => t(code == null ? 'dash.weather.code.unknown' : code >= 61 ? 'dash.weather.code.rain' : code >= 45 ? 'dash.weather.code.fog' : code >= 2 ? 'dash.weather.code.cloudy' : 'dash.weather.code.clear');
  const text =
    days.length === 0
      ? t('dash.weather.none')
      : days.length === 1
        ? t('dash.weather.oneDay', { t: f(days[0]!.tempMaxC), d: desc(days[0]!.weatherCode) })
        : t('dash.weather.twoDays', { t1: f(days[0]!.tempMaxC), d1: desc(days[0]!.weatherCode), t2: f(days[1]!.tempMaxC), d2: desc(days[1]!.weatherCode) });
  return (
    <div className="flex flex-wrap items-start gap-3.5 rounded-xl border px-5 py-[18px]" style={{ background: 'var(--color-dash-orange-soft)', borderColor: 'var(--color-dash-orange-border)' }}>
      <span className="dash-mono text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: 'var(--color-dash-orange)' }}>{t('dash.weather.kicker')}</span>
      <p className="min-w-0 flex-1 basis-[340px] text-sm leading-[1.55] [text-wrap:pretty]" style={{ color: 'var(--color-dash-orange-ink)' }}>
        {text} {t('dash.weather.noPrior')}
      </p>
    </div>
  );
}

function DataHealth({ facts, ledger }: { facts: EventFacts; ledger: ReturnType<typeof ledgerOf> }) {
  const t = useT();
  const { ref, p } = useScrollRamp();
  const removed = Math.max(0, facts.funnel.interactions - facts.funnel.contacts);
  const bridge = [
    { value: fmtInt(Math.round(facts.funnel.interactions * p)), label: t('eventReport.health.raw'), fg: 'var(--color-dash-fg)' },
    { value: `−${fmtInt(Math.round(removed * p))}`, label: t('eventReport.health.removed'), fg: 'var(--color-dash-red)' },
    { value: fmtInt(Math.round(facts.funnel.contacts * p)), label: t('eventReport.health.clean'), fg: 'var(--color-dash-olive)' },
    { value: fmtInt(Math.round(facts.funnel.leads * p)), label: t('eventReport.health.leads'), fg: 'var(--color-dash-olive)' },
  ];
  const consent = env(facts.funnel.marketingOptIns, facts.funnel.leads);
  const gaps = [
    {
      label: t('eventReport.costs.captureRate'),
      value: t('eventReport.health.blocked'),
      fg: 'var(--color-dash-orange)',
      need: t('eventReport.health.trafficNeed'),
    },
    {
      label: t('eventReport.health.marketing'),
      value: consent.state === 'MEASURED' ? fmtPct(consent.value) : t('dash.pending').toLowerCase(),
      fg: consent.state === 'MEASURED' ? 'var(--color-dash-fg)' : 'var(--color-dash-orange)',
      need: consent.state === 'MEASURED' ? t('eventReport.health.marketingNeed', { n: fmtInt(facts.funnel.marketingOptIns) }) : t('eventReport.health.noLeads'),
    },
    {
      label: t('eventReport.health.costs'),
      value: t('eventReport.health.costsValue', { costed: ledger.costedRows, n: ledger.rows.length }),
      fg: ledger.missingCost.length ? 'var(--color-dash-orange)' : 'var(--color-dash-fg)',
      need: ledger.missingCost.length ? t('eventReport.health.costsNeed', { items: ledger.missingCost.map((r) => r.name).join(', ') }) : t('eventReport.health.costsOk'),
    },
    {
      label: t('eventReport.health.inPlay'),
      value: fmtInt(ledger.inPlay),
      fg: ledger.inPlay > 0 ? 'var(--color-dash-orange)' : 'var(--color-dash-olive)',
      need: ledger.inPlay > 0 ? t('eventReport.health.inPlayNeed') : t('eventReport.health.inPlayOk'),
    },
  ];
  return (
    <div ref={ref} className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))]">
      <section className="flex flex-col gap-3.5 rounded-[14px] border p-5" style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)' }}>
        <div className="flex flex-col gap-1.5">
          <span className="dash-mono text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: 'var(--color-dash-olive)' }}>{t('eventReport.health.kicker')}</span>
          <h3 className="text-xl font-bold [text-wrap:pretty]" style={{ color: 'var(--color-dash-fg)' }}>
            {t('eventReport.health.bridgeHead', { raw: fmtInt(facts.funnel.interactions), clean: fmtInt(facts.funnel.contacts) })}
          </h3>
        </div>
        {bridge.map((b) => (
          <div key={b.label} className="flex flex-wrap items-baseline gap-3.5 border-b pb-2.5" style={{ borderColor: 'var(--color-dash-track)' }}>
            <span className="dash-mono w-14 shrink-0 text-[19px] font-bold" style={{ color: b.fg }}>{b.value}</span>
            <span className="min-w-0 flex-1 basis-[180px] text-[13px] [text-wrap:pretty]" style={{ color: 'var(--color-dash-fg-2)' }}>{b.label}</span>
          </div>
        ))}
      </section>
      <section className="flex flex-col gap-3.5 rounded-[14px] border p-5" style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)' }}>
        <h3 className="text-xl font-bold" style={{ color: 'var(--color-dash-fg)' }}>{t('eventReport.health.gapsTitle')}</h3>
        {gaps.map((g) => (
          <div key={g.label} className="flex flex-wrap items-baseline justify-between gap-2.5 border-b pb-[11px]" style={{ borderColor: 'var(--color-dash-track)' }}>
            <span className="min-w-0 flex-1 basis-[150px] text-sm" style={{ color: 'var(--color-dash-fg-2)' }}>{g.label}</span>
            <span className="dash-mono text-[15px] font-bold" style={{ color: g.fg }}>{g.value}</span>
            <span className="basis-full text-xs [text-wrap:pretty]" style={{ color: 'var(--color-dash-fg-muted)' }}>{g.need}</span>
          </div>
        ))}
      </section>
    </div>
  );
}

function Kpi(props: { label: string; value: number; highlight?: boolean }) {
  return (
    <div
      className={
        'rounded-xl border p-4 ' +
        (props.highlight ? 'border-[var(--color-accent-ink)] bg-[var(--color-accent-soft,rgba(178,98,245,0.08))]' : 'border-[var(--color-border-default)] bg-[var(--color-bg-surface)]')
      }
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">{props.label}</div>
      <div className="mt-2 text-2xl font-bold tabular-nums">{props.value.toLocaleString()}</div>
    </div>
  );
}

function Row(props: { label: string; value: number | string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-[var(--color-fg-muted)]">{props.label}</span>
      <span className="font-semibold tabular-nums">{typeof props.value === 'number' ? props.value.toLocaleString() : props.value}</span>
    </div>
  );
}

/**
 * La barra SHOW: fichas con el recuento de cada bloque, que se apagan y
 * encienden de un clic mientras se mira el informe. No se imprime.
 */
function SectionToggles({ counts }: { counts: Partial<Record<ReportSection, string>> }) {
  const t = useT();
  const hidden = useReportSections((s) => s.hidden);
  const toggle = useReportSections((s) => s.toggle);
  const showAll = useReportSections((s) => s.showAll);
  const anyHidden = REPORT_SECTIONS.some((id) => hidden[id]);
  return (
    <div className="flex flex-wrap items-center gap-2.5 print:hidden">
      <span className="dash-mono pr-1 text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: 'var(--color-dash-fg-muted)' }}>{t('eventReport.sections.label')}</span>
      {REPORT_SECTIONS.map((id: ReportSection) => {
        const on = !hidden[id];
        return (
          <button
            key={id}
            type="button"
            onClick={() => toggle(id)}
            aria-pressed={on}
            className="flex items-center gap-[7px] rounded-full border px-3.5 py-[7px] text-[13px] font-semibold transition-colors"
            style={on ? { background: 'var(--color-dash-lime)', borderColor: 'var(--color-dash-lime)', color: 'var(--color-dash-ink)' } : { background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border-2)', color: 'var(--color-dash-fg-2)' }}
          >
            {t(`eventReport.sections.${id}`)}
            {counts[id] && (
              <span className="dash-mono text-[9px] font-bold uppercase tracking-[0.06em]" style={{ color: on ? 'rgba(17,17,17,0.55)' : 'var(--color-dash-fg-subtle)' }}>
                {counts[id]}
              </span>
            )}
          </button>
        );
      })}
      {anyHidden && (
        <button type="button" onClick={showAll} className="ml-auto text-xs font-semibold hover:underline" style={{ color: 'var(--color-dash-olive)' }}>
          {t('eventReport.show.everything')}
        </button>
      )}
    </div>
  );
}
